import { useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Heading, VStack, Text, Box } from '@primerai/thread';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import * as yup from 'yup';

import { ReactFormTextInput } from '~/components/form/input';
import { FORM_ERROR_MESSAGES, TEXT_SIZE, HEADING_SIZE } from '~/constants';
import { getLoginUsername } from '~/store/login/selectors';
import { Actions } from '~/store/password/actions';
import { getResetLinkIsLoading } from '~/store/password/selectors';

const defaultCaption = 'Oops, looks like your link has expired';

type FormValues = {
  email: string;
};

const validationSchema: yup.ObjectSchema<FormValues> = yup.object().shape({
  email: yup
    .string()
    .email(FORM_ERROR_MESSAGES.EMAIL.REQUIRED)
    .required(FORM_ERROR_MESSAGES.EMAIL.REQUIRED),
});

const RequestReset = ({ caption = defaultCaption }: { caption?: string }) => {
  const preSetEmail = useSelector(getLoginUsername);
  const isLoading = useSelector(getResetLinkIsLoading);
  const dispatch = useDispatch();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      email: preSetEmail || '',
    },
    resolver: yupResolver(validationSchema) as any,
  });

  const requestResetLink = useCallback(
    (username: string) =>
      dispatch(Actions.FORGOT_PASSWORD_REQUEST({ username })),
    [dispatch],
  );

  const onSubmitValidForm: SubmitHandler<FormValues> = useCallback(
    ({ email }) => {
      if (email) {
        requestResetLink(email);
      }
    },
    [requestResetLink],
  );

  const onSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    void handleSubmit(onSubmitValidForm)(e);
  };

  return (
    <VStack>
      <Box mb="$7">
        <Heading mb="$2" size={HEADING_SIZE.LARGE}>
          {caption}
        </Heading>
        <Text size={TEXT_SIZE.DEFAULT}>
          Enter your email address below and we&apos;ll send you a link to reset
          your password
        </Text>
      </Box>
      <form onSubmit={onSubmit}>
        <Box mb="$8" minHeight="64px">
          <ReactFormTextInput
            control={control}
            errors={errors}
            name="email"
            placeholder="Email address"
          />
        </Box>
        <Button
          appearance="primary"
          isLoading={isLoading}
          type="submit"
          width="100%"
        >
          Send password reset link
        </Button>
      </form>
    </VStack>
  );
};

export default RequestReset;
